/**
 * Component Codes - Unified component definitions
 *
 * Used by:
 * - Backend: Error codes (CCXSEEEE format), tracing/logging
 * - Frontend: Display error source, filter logs
 *
 * IMPORTANT: These are FIXED VALUES - never reorder or reassign!
 */

/**
 * Component codes enum
 */
export enum ComponentCode {
    // Core (01-09)
    HANDLER = 1,       // 01 - Request handler layer
    AUTH = 2,          // 02 - Authentication/authorization
    VALIDATION = 3,    // 03 - Request validation
    API_LAYER = 4,     // 04 - API request/response logging

    // Business (10-19)
    ANALYSIS = 10,     // 10 - Question analysis
    HINT = 11,         // 11 - Hint generation
    EVAL = 12,         // 12 - Evaluation system
    MODEL_RUN = 13,    // 13 - Model run management
    VOICE_CHAT = 14, // 14 - Voice chat
    STUDY_SESSION = 15,      // 15 - Study session management
    MOCK_TEST = 16,          // 16 - Mock test management
    TEST_SESSION = 17,       // 17 - Test session management
    SEND_EMAIL = 18,          // 18 - Email sending
    PROGRESS_REPORT = 19,     // 19 - Progress report generation
    TEACHER_STUDENTS = 20,     // 20 - Teacher-student management
    STUDENT_SOLUTION = 21,     // 21 - In-progress student-solution drafts (decoupled save/analyze)
    DUEL = 22,                 // 22 - Head-to-head duels / challenges

    // External (30-39)
    LLM = 30,          // 30 - LLM provider base
    LLM_OPENAI = 31,   // 31 - OpenAI provider
    LLM_ANTHROPIC = 32, // 32 - Anthropic/Claude provider
    LLM_GOOGLE = 33,   // 33 - Google/Gemini provider

    // Admin (40-49)
    ADMIN = 40,        // 40 - Admin operations
    SETTINGS = 41,     // 41 - Settings management

    // Messaging (45-49)
    MESSAGING = 45,    // 45 - WhatsApp / messaging

    // Infrastructure (50-59)
    TRACING = 50,      // 50 - Tracing system itself
    ASYNC_JOB = 51,    // 51 - Async job execution framework (dispatcher / registry / handlers)

    // Web (60-69)
    WEB_UI = 60,           // 60 - General UI/rendering
    WEB_API = 61,          // 61 - API client layer (fetch wrappers)
    WEB_AUTH = 62,         // 62 - Auth/session management
    WEB_NAVIGATION = 63,   // 63 - Routing/navigation
    WEB_CANVAS = 64,       // 64 - Canvas (student app)
    WEB_STATE = 65,         // 65 - State management / React Query
    WEB_PERF = 66,          // 66 - Performance metrics (page load, etc.)

    // Data (25-29)
    DATABASE = 70,     // 70 - CosmosDB operations
    BLOB = 71,         // 71 - Blob storage operations

}

/**
 * String keys for component lookup (used in logging config, admin UI)
 */
export const ComponentKey: Record<ComponentCode, string> = {
    [ComponentCode.HANDLER]: 'handler',
    [ComponentCode.AUTH]: 'auth',
    [ComponentCode.VALIDATION]: 'validation',
    [ComponentCode.API_LAYER]: 'api',
    [ComponentCode.ANALYSIS]: 'analysis',
    [ComponentCode.HINT]: 'hint',
    [ComponentCode.EVAL]: 'eval',
    [ComponentCode.MODEL_RUN]: 'modelrun',
    [ComponentCode.VOICE_CHAT]: 'voicechat',
    [ComponentCode.STUDY_SESSION]: 'studysession',
    [ComponentCode.MOCK_TEST]: 'mocktest',
    [ComponentCode.TEST_SESSION]: 'testsession',
    [ComponentCode.SEND_EMAIL]: 'sendemail',
    [ComponentCode.PROGRESS_REPORT]: 'progressreport',
    [ComponentCode.TEACHER_STUDENTS]: 'teacherstudents',
    [ComponentCode.STUDENT_SOLUTION]: 'studentsolution',
    [ComponentCode.DUEL]: 'duel',
    [ComponentCode.DATABASE]: 'database',
    [ComponentCode.BLOB]: 'blob',
    [ComponentCode.LLM]: 'llm',
    [ComponentCode.LLM_OPENAI]: 'llm.openai',
    [ComponentCode.LLM_ANTHROPIC]: 'llm.anthropic',
    [ComponentCode.LLM_GOOGLE]: 'llm.google',
    [ComponentCode.ADMIN]: 'admin',
    [ComponentCode.MESSAGING]: 'messaging',
    [ComponentCode.SETTINGS]: 'settings',
    [ComponentCode.TRACING]: 'tracing',
    [ComponentCode.ASYNC_JOB]: 'asyncjob',
    [ComponentCode.WEB_UI]: 'web.ui',
    [ComponentCode.WEB_API]: 'web.api',
    [ComponentCode.WEB_AUTH]: 'web.auth',
    [ComponentCode.WEB_NAVIGATION]: 'web.navigation',
    [ComponentCode.WEB_CANVAS]: 'web.canvas',
    [ComponentCode.WEB_STATE]: 'web.state',
    [ComponentCode.WEB_PERF]: 'web.perf'
};

/**
 * Display names for UI
 */
export const ComponentName: Record<ComponentCode, string> = {
    [ComponentCode.HANDLER]: 'Handler',
    [ComponentCode.AUTH]: 'Authentication',
    [ComponentCode.VALIDATION]: 'Validation',
    [ComponentCode.API_LAYER]: 'API Layer',
    [ComponentCode.ANALYSIS]: 'Analysis',
    [ComponentCode.HINT]: 'Hints',
    [ComponentCode.EVAL]: 'Evaluation',
    [ComponentCode.MODEL_RUN]: 'Model Run',
    [ComponentCode.VOICE_CHAT]: 'VoiceChat',
    [ComponentCode.STUDY_SESSION]: 'StudySession',
    [ComponentCode.MOCK_TEST]: 'MockTest',
    [ComponentCode.TEST_SESSION]: 'TestSession',
    [ComponentCode.SEND_EMAIL]: 'Send Email',
    [ComponentCode.PROGRESS_REPORT]: 'Progress Report',
    [ComponentCode.TEACHER_STUDENTS]: 'Teacher Students',
    [ComponentCode.STUDENT_SOLUTION]: 'Student Solution',
    [ComponentCode.DUEL]: 'Duel',
    [ComponentCode.DATABASE]: 'Database',
    [ComponentCode.BLOB]: 'Storage',
    [ComponentCode.LLM]: 'LLM',
    [ComponentCode.LLM_OPENAI]: 'OpenAI',
    [ComponentCode.LLM_ANTHROPIC]: 'Anthropic',
    [ComponentCode.LLM_GOOGLE]: 'Google',
    [ComponentCode.ADMIN]: 'Admin',
    [ComponentCode.MESSAGING]: 'Messaging',
    [ComponentCode.SETTINGS]: 'Settings',
    [ComponentCode.TRACING]: 'Tracing',
    [ComponentCode.ASYNC_JOB]: 'Async Job',
    [ComponentCode.WEB_UI]: 'Web UI',
    [ComponentCode.WEB_API]: 'Web API',
    [ComponentCode.WEB_AUTH]: 'Web Auth',
    [ComponentCode.WEB_NAVIGATION]: 'Web Navigation',
    [ComponentCode.WEB_CANVAS]: 'Web Canvas',
    [ComponentCode.WEB_STATE]: 'Web State',
    [ComponentCode.WEB_PERF]: 'Web Perf'
};

/**
 * Get component name for display
 */
export function getComponentName(code: ComponentCode): string {
    return ComponentName[code] || `Component ${code}`;
}

/**
 * Get component key for config/logging
 */
export function getComponentKey(code: ComponentCode): string {
    return ComponentKey[code] || `component-${code}`;
}
